import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Bell, BellRing, Search, Sparkles, Users, Calendar, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

const typeBadge = { manga: 'bg-rose-100 text-rose-700 border-rose-200', bd: 'bg-amber-100 text-amber-800 border-amber-200' };
const typeLabel = { manga: 'Manga', bd: 'BD' };

function SeriesCard({ s, followed, onFollow, index }) {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04, duration: 0.3 }}
      className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col"
      data-testid={`series-card-${s.id}`}>
      {s.image_url ? (
        <div className="h-40 overflow-hidden"><img src={s.image_url} alt={s.name} className="h-full w-full object-cover" /></div>
      ) : (
        <div className={`h-28 flex items-center justify-center ${s.series_type === 'bd' ? 'bg-amber-50' : 'bg-rose-50'}`}>
          <BookOpen className={`h-10 w-10 ${s.series_type === 'bd' ? 'text-amber-200' : 'text-rose-200'}`} />
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <Badge className={`text-xs border ${typeBadge[s.series_type] || typeBadge.manga}`}>{typeLabel[s.series_type] || 'Manga'}</Badge>
          {s.publisher && <Badge variant="secondary" className="text-xs">{s.publisher}</Badge>}
        </div>
        <h3 className="font-semibold text-slate-900 text-lg leading-snug">{s.name}</h3>
        {s.author && <p className="text-sm text-slate-500 mt-0.5">{s.author}</p>}
        <div className="mt-3 space-y-1 text-sm text-slate-500 flex-1">
          <div className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /><span data-testid={`series-followers-${s.id}`}>{s.follower_count} lecteur{s.follower_count > 1 ? 's suivent' : ' suit'} cette série</span></div>
          {s.last_release && (
            <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /><span>Dernière sortie : {s.last_release.volume_label} ({formatDate(s.last_release.release_date)})</span></div>
          )}
        </div>
        <Button
          onClick={() => onFollow(s)}
          disabled={followed}
          variant={followed ? 'outline' : 'default'}
          className={`mt-4 rounded-full w-full ${followed ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'bg-rose-600 hover:bg-rose-700'}`}
          data-testid={`follow-series-btn-${s.id}`}
        >
          {followed ? <><BellRing className="h-4 w-4 mr-2" /> Vous suivez cette série</> : <><Bell className="h-4 w-4 mr-2" /> Suivre cette série</>}
        </Button>
      </div>
    </motion.div>
  );
}

export default function Series() {
  const [series, setSeries] = useState([]);
  const [search, setSearch] = useState('');
  const [email, setEmail] = useState(localStorage.getItem('wishlist_email') || '');
  const [followedIds, setFollowedIds] = useState(new Set());
  const [dialog, setDialog] = useState({ open: false, series: null });
  const [propose, setPropose] = useState({ name: '', series_type: 'manga' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/series${search ? `?search=${encodeURIComponent(search)}` : ''}`).then(r => setSeries(r.data)).catch(console.error);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!email) return;
    api.get(`/series/follows?email=${encodeURIComponent(email)}`).then(r => setFollowedIds(new Set(r.data.map(f => f.series_id)))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doFollow = async (payload) => {
    if (!email || !email.includes('@')) { toast.error("Veuillez entrer une adresse email valide"); return null; }
    setSubmitting(true);
    try {
      const res = await api.post('/series/follow', { email, ...payload });
      localStorage.setItem('wishlist_email', email);
      setFollowedIds(prev => new Set([...prev, res.data.series_id]));
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const confirmFollow = async () => {
    const res = await doFollow({ series_id: dialog.series.id });
    if (res) {
      toast.success(`Vous serez prévenu(e) à chaque nouvelle sortie de ${dialog.series.name}`);
      setSeries(prev => prev.map(s => s.id === dialog.series.id ? { ...s, follower_count: s.follower_count + 1 } : s));
      setDialog({ open: false, series: null });
    }
  };

  const submitPropose = async (e) => {
    e.preventDefault();
    if (!propose.name.trim()) { toast.error("Indiquez le nom de la série"); return; }
    const res = await doFollow({ series_name: propose.name.trim(), series_type: propose.series_type });
    if (res) {
      toast.success(res.series.status === 'proposee'
        ? `Demande enregistrée ! Nous vous préviendrons dès la prochaine sortie de ${res.series.name}`
        : `Vous suivez désormais ${res.series.name}`);
      setPropose({ name: '', series_type: 'manga' });
    }
  };

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
          <div className="lg:col-span-7">
            <span className="font-accent text-2xl text-rose-500">Ne ratez plus aucun tome</span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-2">Suivre mes séries</h1>
            <p className="text-base md:text-lg text-slate-500 mt-3 max-w-xl">
              Dites-nous quelles séries BD & Manga vous suivez : dès qu'un nouveau tome sort, nous vous prévenons par email et vous pouvez le réserver en un clic.
            </p>
          </div>
          <div className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl p-5" data-testid="series-email-box">
            <Label htmlFor="series-email" className="text-xs font-bold uppercase tracking-widest text-slate-500">Votre email</Label>
            <Input id="series-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" className="mt-2 rounded-lg" data-testid="series-email-input" />
            <p className="text-xs text-slate-400 mt-2">Utilisé uniquement pour vous prévenir des sorties. <Link to="/wishlist" className="text-violet-600 hover:underline" data-testid="series-manage-link">Gérer mes suivis</Link></p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="relative mb-6">
              <Search className="h-4 w-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher une série, un auteur, un éditeur..." className="pl-11 rounded-full h-12" data-testid="series-search-input" />
            </div>
            {series.length === 0 ? (
              <div className="text-center py-20 bg-white border border-slate-200 rounded-2xl" data-testid="series-empty">
                <BookOpen className="h-14 w-14 mx-auto text-slate-200" />
                <p className="mt-4 text-slate-500 text-lg">{search ? 'Aucune série ne correspond à votre recherche' : 'Aucune série référencée pour le moment'}</p>
                <p className="text-slate-400 text-sm mt-1">Proposez la vôtre dans le formulaire ci-contre</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="series-grid">
                {series.map((s, i) => <SeriesCard key={s.id} s={s} index={i} followed={followedIds.has(s.id)} onFollow={(sr) => setDialog({ open: true, series: sr })} />)}
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <form onSubmit={submitPropose} className="bg-slate-900 text-white rounded-2xl p-6 lg:sticky lg:top-28" data-testid="propose-series-form">
              <div className="flex items-center gap-2 text-amber-400"><Sparkles className="h-5 w-5" /><span className="font-accent text-xl">Votre série n'est pas là ?</span></div>
              <h2 className="text-base md:text-lg font-bold mt-2">Proposez une série à suivre</h2>
              <p className="text-sm text-slate-300 mt-2">Saisissez son nom : nous l'ajoutons à notre veille et vous prévenons dès la prochaine sortie.</p>
              <div className="mt-5 space-y-3">
                <div>
                  <Label className="text-xs text-slate-400">Nom de la série</Label>
                  <Input value={propose.name} onChange={e => setPropose(p => ({ ...p, name: e.target.value }))} placeholder="Ex : Jujutsu Kaisen, Blacksad..." className="mt-1 rounded-lg bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" data-testid="propose-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Type</Label>
                  <Select value={propose.series_type} onValueChange={v => setPropose(p => ({ ...p, series_type: v }))}>
                    <SelectTrigger className="mt-1 rounded-lg bg-slate-800 border-slate-700 text-white" data-testid="propose-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="manga">Manga</SelectItem><SelectItem value="bd">BD</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="mt-5 w-full rounded-full bg-amber-400 hover:bg-amber-500 text-slate-900 font-semibold" data-testid="propose-submit-btn">
                <Heart className="h-4 w-4 mr-2" /> Me prévenir des sorties
              </Button>
            </form>
          </aside>
        </div>
      </div>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="sm:max-w-md" data-testid="follow-dialog">
          <DialogHeader>
            <DialogTitle>Suivre {dialog.series?.name}</DialogTitle>
            <DialogDescription>Nous vous enverrons un email à chaque nouvelle sortie de cette série.</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="follow-email" className="text-xs font-medium text-slate-500">Votre email</Label>
            <Input id="follow-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" className="mt-1 rounded-lg" data-testid="follow-email-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialog({ open: false, series: null })} data-testid="follow-cancel-btn">Annuler</Button>
            <Button className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={confirmFollow} disabled={submitting} data-testid="follow-confirm-btn">
              <Bell className="h-4 w-4 mr-2" /> Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
